import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

/** Auth state.
 * Sessions are backed by an httpOnly `access_token` cookie set by the backend
 * on register/login and cleared on logout. The browser attaches the cookie
 * automatically (see `withCredentials` in `lib/api.js`), so we never handle
 * the token value in JavaScript. This prevents XSS-based token theft. */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        // 401 = not logged in (normal). Anything else (5xx, network) is a real
        // failure and should be visible in DevTools even on default log filters.
        const s = err?.response?.status;
        if (s && s !== 401) console.warn("Auth check failed:", s, err?.message);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Cookie was set server-side; nothing to persist client-side.
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const register = async (name, email, password, refCode = null) => {
    const payload = { name, email, password };
    if (refCode) payload.ref_code = refCode;
    const { data } = await api.post("/auth/register", payload);
    // Cookie was set server-side; nothing to persist client-side.
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) {
      // Server-side logout is best-effort — always clear the client state below.
      console.debug("Logout endpoint error (client still clears session):", err?.message);
    }
    // Legacy cleanup for anyone still on the old localStorage build.
    try { localStorage.removeItem("token"); }
    catch (err) { console.debug("Legacy token cleanup skipped:", err?.message); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
